import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { ArcPolyline } from '@src/core/controllers';
import { RouteWaypointLabel } from '@src/features/order/pages/upsert-mission-page/components';
import { getOutboundLeg } from '@src/features/order/pages/upsert-mission-page/utils';
import { FlightDrone } from '@src/features/order/components';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';
import { TakeOffToEnRouteMapProps } from './types';
import { InfoWindow, Marker } from '@react-google-maps/api';
import { ArcButton, ArcCellInfo, ArcStack, ArcText } from '@src/core/ui';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import gohere from '@src/assets/gohere.svg';
import takeoff from '@src/assets/takeoff.svg';
import destionation from '@src/assets/destination.svg';
import { useFlightProvider } from '@src/features/order/provider';
import { OrderStatusType } from '@src/core/features';

const TakeOffToEnRouteMap: React.FC<TakeOffToEnRouteMapProps> = (props) => {
  const { goTo, viewer, points = [], position, removePosition, resetGoHere, missionId: propMissionId } = props;
  const { missionId: urlMissionId, droneSerial } = useParams();
  const [prevFlightData, setPrevFlightData] = React.useState<{latitude: string, longitude: string} | null>(null);
  
  // Use prop missionId if provided (order form context), otherwise use URL params
  const missionId = propMissionId || urlMissionId;
  
  const { data, isLoading } = useGetOrderMissionQuery(missionId, {
    skip: !missionId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const { flightData } = useFlightProvider();
  const { confirm } = useConfirm();
  const { setCameraSource } = useCameraLayout();

  const serialNumber = data?.drone?.serialNumber;

  useEffect(() => {
    if (serialNumber) {
      setCameraSource({
        serialNumber,
        isViewer: viewer,
        resetGoHere,
        isEnRoute: data?.status === OrderStatusType.EN_ROUTE,
      });
    } else {
      setCameraSource(null);
    }
  }, [serialNumber, viewer, resetGoHere, data?.status]);

  useEffect(() => {
    if (!flightData?.latitude || !flightData?.longitude || flightData.serialNumber !== droneSerial) {
      return;
    }
    const latitude = flightData.latitude.toString();
    const longitude = flightData.longitude.toString();
    setPrevFlightData((prev) => {
      if (prev?.latitude === latitude && prev?.longitude === longitude) return prev;
      return { latitude, longitude };
    });
  }, [flightData, droneSerial]);

  if (isLoading || !missionId || !data) {
    return (
      <React.Fragment>
        {/* Show loading skeleton or spinner while data loads */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          color: '#666'
        }}>
          Loading mission data...
        </div>
      </React.Fragment>
    );
  }


  const path = (data?.waypoints ?? []).map((wp) => ({ lat: Number(wp.latitude), lng: Number(wp.longitude) }));
  
  const flightLat = flightData?.serialNumber === droneSerial ? Number(flightData?.latitude) : Number(prevFlightData?.latitude);
  const flightLng = flightData?.serialNumber === droneSerial ? Number(flightData?.longitude) : Number(prevFlightData?.longitude);

  const [ startPoint ] = data?.waypoints ?? [];

  const startLat = Number(startPoint?.latitude); 
  const startLng = Number(startPoint?.longitude);

  // The saved route is a round trip whose return mirrors the outbound, so number only the
  // outbound waypoints — otherwise the overlapping return points would double-label the route.
  const numberedWaypoints = getOutboundLeg(data?.waypoints ?? [])
    .map((wp, idx) => ({ wp, idx }))
    .filter(({ wp, idx }) => idx !== 0 && !wp.orderId)
    .map(({ wp }, index) => ({ wp, waypointNumber: index + 1 }));

  const goHere = async (position: google.maps.LatLngLiteral) => {
    const confirmed = await confirm(
      'The drone will fly to the selected location.',
      'Confirm Go Here?'
    );
    if (!confirmed) return;
    goTo?.(position);
    removePosition?.();
  };
  
  return (
    <React.Fragment>

      <ArcPolyline.future path={path} />

      <FlightDrone
        drone={data?.drone}
        isOnMission={data?.status === OrderStatusType.EN_ROUTE}
      />
      
      {data?.orders?.map((order) => {
        const lat = Number(order.deliveryLocation.latitude);
        const lng = Number(order.deliveryLocation.longitude);

        if (isNaN(lat) || isNaN(lng)) return null;

        return (
          <Marker 
            key={order.id} 
            position={{ lat, lng }} 
            icon={{ 
              url: destionation,  
              scaledSize: new window.google.maps.Size(100, 50),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(17, 44),  
            }}/>
        );
      })}

      <Marker 
        position={{ lat: startLat, lng: startLng }} 
        icon={{ 
          url: takeoff, 
          scaledSize: new window.google.maps.Size(80, 40),
          origin: new window.google.maps.Point(0, 0),
          anchor: new window.google.maps.Point(10, 20), 
        }} 
      />

      {numberedWaypoints.map(({ wp, waypointNumber }) => {
        const lat = Number(wp.latitude);
        const lng = Number(wp.longitude);
        if (isNaN(lat) || isNaN(lng)) return null;
        return (
          <RouteWaypointLabel
            key={`wp-${waypointNumber}`}
            waypointNumber={waypointNumber}
            position={{ lat, lng }}
          />
        );
      })}

      {points.map((point) => (
        <Marker 
          key={point.id} 
          position={point.position}  
          icon={{
            url: gohere,
            scaledSize: new window.google.maps.Size(80, 40),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(12, 38),
          }} />
      ))}

      {position && (
        <InfoWindow 
          position={position} 
          onCloseClick={removePosition} 
          options={{
            minWidth: 250,
          }}>
          <ArcStack spacing={2}> 
            <ArcStack spacing={2} direction='row'>
              <ArcCellInfo label='LATITUDE'> 
                <ArcText.h5 color='text.night'> {position.lat.toFixed(5)} </ArcText.h5>
              </ArcCellInfo>
              <ArcCellInfo label='LONGITUDE'> 
                <ArcText.h5 color='text.night'> {position.lng.toFixed(5)} </ArcText.h5>
              </ArcCellInfo>
            </ArcStack>
            <ArcButton.brand 
              onClick={() => goHere(position)}> 
              Go Here 
            </ArcButton.brand>
          </ArcStack>
        </InfoWindow>
      )}

      <ArcPolyline.error path={
        [
          {lat: flightLat || Number(data?.drone?.latitude), lng: flightLng || Number(data?.drone?.longitude) }, 
          ...points.map((point) => point.position)
        ]
      } />
    </React.Fragment>
  );
};

export default TakeOffToEnRouteMap;